import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import adminApi from '../../../services/adminService';

// Owns all state + handlers for the Security page: database backups,
// audit logs (with filters/summary), and the invalid-product cleanup tool.
export const useSecurity = () => {
  // Backup state
  const [backups, setBackups] = useState([]);
  const [backupStats, setBackupStats] = useState(null);
  const [backupsLoading, setBackupsLoading] = useState(true);
  const [createBackupOpen, setCreateBackupOpen] = useState(false);
  const [backupDescription, setBackupDescription] = useState('');
  const [creatingBackup, setCreatingBackup] = useState(false);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditSummary, setAuditSummary] = useState(null);
  const failedActionsTotal = useMemo(
    () => Object.values(auditSummary?.failed_actions || {}).reduce((a, b) => a + b, 0),
    [auditSummary]
  );
  const [auditFilters, setAuditFilters] = useState({
    action: 'all',
    resource_type: 'all',
    user_email: '',
    status: 'all',
    limit: 50,
  });
  const [availableActions, setAvailableActions] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);

  // Cleanup state
  const [invalidProducts, setInvalidProducts] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);

  useEffect(() => {
    loadBackups();
    loadBackupStats();
    loadAuditLogs();
    loadAuditSummary();
    loadAvailableActions();
    // Mount-only initial load. Audit logs intentionally reload via the
    // "Apply Filters" button, not on every filter keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Backup functions ──────────────────────
  const loadBackups = async () => {
    try {
      const response = await adminApi.get('/admin/security/backups');
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
      toast.error('Failed to load backups');
    } finally {
      setBackupsLoading(false);
    }
  };

  const loadBackupStats = async () => {
    try {
      const response = await adminApi.get('/admin/security/backups/stats');
      setBackupStats(response.data);
    } catch (error) {
      console.error('Error loading backup stats:', error);
    }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      const response = await adminApi.post('/admin/security/backups', {
        description: backupDescription || 'Manual backup',
      });
      toast.success(`Backup created: ${response.data.total_records} records saved`);
      setCreateBackupOpen(false);
      setBackupDescription('');
      loadBackups();
      loadBackupStats();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const downloadBackup = async (backupId) => {
    try {
      const response = await adminApi.get(`/admin/security/backups/${backupId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${backupId}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup downloaded');
      loadAuditLogs(); // Refresh to show download log
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup');
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;
    try {
      await adminApi.delete(`/admin/security/backups/${backupId}`);
      toast.success('Backup deleted');
      loadBackups();
      loadBackupStats();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    }
  };

  // ─── Audit log functions ──────────────────────
  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilters.action && auditFilters.action !== 'all') params.append('action', auditFilters.action);
      if (auditFilters.resource_type && auditFilters.resource_type !== 'all') params.append('resource_type', auditFilters.resource_type);
      if (auditFilters.user_email) params.append('user_email', auditFilters.user_email);
      if (auditFilters.status && auditFilters.status !== 'all') params.append('status', auditFilters.status);
      params.append('limit', auditFilters.limit.toString());

      const response = await adminApi.get(`/admin/security/audit-logs?${params}`);
      setAuditLogs(response.data.logs || []);
      setAuditTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  const loadAuditSummary = async () => {
    try {
      const response = await adminApi.get('/admin/security/audit-logs/summary?days=7');
      setAuditSummary(response.data);
    } catch (error) {
      console.error('Error loading audit summary:', error);
    }
  };

  const loadAvailableActions = async () => {
    try {
      const response = await adminApi.get('/admin/security/audit-logs/actions');
      setAvailableActions(response.data.actions || []);
      setAvailableResources(response.data.resource_types || []);
    } catch (error) {
      console.error('Error loading actions:', error);
    }
  };

  // ─── Cleanup functions ──────────────────────
  const previewCleanup = async () => {
    setCleanupLoading(true);
    try {
      const response = await adminApi.get('/admin/products/cleanup/preview');
      setCleanupPreview(response.data);
      setInvalidProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading cleanup preview:', error);
      toast.error('Failed to load cleanup preview');
    } finally {
      setCleanupLoading(false);
    }
  };

  const runCleanup = async () => {
    if (!window.confirm(`Are you sure you want to delete ${invalidProducts.length} invalid products? This cannot be undone.`)) return;
    setCleanupLoading(true);
    try {
      const response = await adminApi.delete('/admin/products/cleanup');
      toast.success(response.data.message);
      setInvalidProducts([]);
      setCleanupPreview(null);
    } catch (error) {
      console.error('Error running cleanup:', error);
      toast.error('Failed to run cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  return {
    // backups
    backups,
    backupStats,
    backupsLoading,
    createBackupOpen,
    setCreateBackupOpen,
    backupDescription,
    setBackupDescription,
    creatingBackup,
    loadBackups,
    createBackup,
    downloadBackup,
    deleteBackup,
    // audit
    auditLogs,
    auditTotal,
    auditLoading,
    auditSummary,
    failedActionsTotal,
    auditFilters,
    setAuditFilters,
    availableActions,
    availableResources,
    loadAuditLogs,
    // cleanup
    invalidProducts,
    cleanupLoading,
    cleanupPreview,
    previewCleanup,
    runCleanup,
  };
};
